/**
 * folder-tree-index.ts
 *
 * Task 1 of FolderTreeV3: Types and indexing utility for the flat v2/folder_with_tags API.
 *
 * Converts flat API rows into O(1) lookup Maps so every consumer (search,
 * selection, visible-row computation) avoids recursive tree traversal.
 *
 * Algorithm overview:
 *   Pass 1 – O(n rows): Build foldersById, tagsByKey, tagsByName, tagKeysByFolderId.
 *             Folder metadata is extracted from the first row seen for each folder_id.
 *   Pass 2 – O(folders), BFS: Compute level, ancestorIds, childFolderIds, rootFolderIds.
 *   Pass 3 – O(folders), iterative post-order DFS: Compute allDescendantFolderIds,
 *             allDescendantTagKeys bottom-up.
 */

import type { FlatFolderTagRow, SubSystem, Tag } from '@controlrooms/models';
import type { SubSystemParentRef } from '@controlrooms/models';

// ---------------------------------------------------------------------------
// Exported types
// ---------------------------------------------------------------------------

/** A folder enriched with pre-computed lookup data. */
export interface IndexedFolder {
  folderId: number;
  parentId: number | null;
  name: string;
  infraDisplayName: string;
  description: string;
  infraTypeId: number;
  hasModel: boolean;
  hasLimits: boolean;
  hasLimitsV2: boolean;
  hasMonitorModel: boolean;
  hasAlerts: boolean;
  /** Direct child folder IDs, in backend-guaranteed source order. */
  childFolderIds: number[];
  /** Composite keys (`${folderId}::${tagName}`) for tags directly in this folder. */
  directTagKeys: string[];
  /** Depth from plant root (root = 0, root's children = 1, …). */
  level: number;
  /** Ordered [rootId, …, parentId] chain — used for search auto-expand. */
  ancestorIds: number[];
  /** Pre-lowercased infra_display_name for fast search matching. */
  searchText: string;
}

/** A tag enriched with pre-computed lookup data. */
export interface IndexedTag {
  /** Composite key: `${folderId}::${tagName}` */
  key: string;
  folderId: number;
  tagName: string;
  tagDisplayName: string;
  tagDescription: string;
  uom: string;
  isNumeric: boolean;
  isContinuous: boolean;
  isSampled: boolean;
  isDerived: boolean;
  isState: boolean;
  tagCategoryName: string;
  hasLimits: boolean;
  hasLimitsV2: boolean;
  hasModel: boolean;
  hasMonitorModel: boolean;
  hasAlertsTags: boolean;
  /** Same depth as the containing folder (used for indentation). */
  level: number;
  /** Pre-lowercased `${tagDisplayName} ${tagDescription} ${uom}` for fast search. */
  searchText: string;
}

/** All pre-built Maps for O(1) lookups across the entire folder/tag tree. */
export interface FolderTreeIndex {
  foldersById: Map<number, IndexedFolder>;
  tagsByKey: Map<string, IndexedTag>;
  /** tagName → all IndexedTags with that name (across different folders). */
  tagsByName: Map<string, IndexedTag[]>;
  /** folderId → direct tag composite keys. */
  tagKeysByFolderId: Map<number, string[]>;
  /** Direct children of the plant root (level-1 folders), in source order. */
  rootFolderIds: number[];
  /** The folder whose parent_folder_id is null; null when rows is empty. */
  plantRootId: number | null;
  /** folderId → ALL descendant folder IDs (recursive). */
  allDescendantFolderIds: Map<number, number[]>;
  /** folderId → ALL descendant tag composite keys (recursive). */
  allDescendantTagKeys: Map<number, string[]>;
  /** folderId → parentFolderId (absent for plant root). */
  parentMap: Map<number, number>;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Build an empty IndexedFolder from the first row seen for a given folder_id. */
function createIndexedFolder(row: FlatFolderTagRow): IndexedFolder {
  return {
    folderId: row.folder_id,
    parentId: row.parent_folder_id,
    name: row.name,
    infraDisplayName: row.infra_display_name,
    description: row.description ?? '',
    infraTypeId: row.infra_type_id,
    hasModel: row.has_model,
    hasLimits: row.has_limits,
    hasLimitsV2: row.has_limits_v2,
    hasMonitorModel: row.has_monitor_model,
    hasAlerts: row.has_alerts ?? false,
    childFolderIds: [],
    directTagKeys: [],
    // level and ancestorIds are set during Pass 2 BFS
    level: 0,
    ancestorIds: [],
    searchText: row.infra_display_name.toLowerCase(),
  };
}

/** Build an IndexedTag from a row that has a non-null tag_name. */
function createIndexedTag(row: FlatFolderTagRow, tagName: string, folderLevel: number): IndexedTag {
  const tagDisplayName = row.tag_display_name ?? tagName;
  const tagDescription = row.tag_description ?? '';
  const uom = row.uom ?? '';

  return {
    key: `${row.folder_id}::${tagName}`,
    folderId: row.folder_id,
    tagName,
    tagDisplayName,
    tagDescription,
    uom,
    isNumeric: row.is_numeric ?? false,
    isContinuous: row.is_continuous ?? false,
    isSampled: row.is_sampled ?? false,
    isDerived: row.is_derived ?? false,
    isState: row.is_state ?? false,
    tagCategoryName: row.tag_category_name ?? '',
    hasLimits: row.has_limits,
    hasLimitsV2: row.has_limits_v2,
    hasModel: row.has_model,
    hasMonitorModel: row.has_monitor_model,
    hasAlertsTags: row.has_alerts_tags ?? false,
    level: folderLevel,
    searchText: `${tagDisplayName} ${tagDescription} ${uom}`.toLowerCase(),
  };
}

// ---------------------------------------------------------------------------
// Main builder
// ---------------------------------------------------------------------------

/**
 * Convert a flat array of API rows into a fully-indexed FolderTreeIndex.
 *
 * The build is a 3-pass algorithm:
 * 1. O(n) scan → build foldersById, tagsByKey, tagsByName, tagKeysByFolderId.
 * 2. O(folders) BFS from plant root → level, ancestorIds, childFolderIds, rootFolderIds.
 * 3. O(folders) iterative post-order DFS → allDescendantFolderIds, allDescendantTagKeys.
 */
export function buildFolderTreeIndex(rows: FlatFolderTagRow[]): FolderTreeIndex {
  const emptyIndex: FolderTreeIndex = {
    foldersById: new Map(),
    tagsByKey: new Map(),
    tagsByName: new Map(),
    tagKeysByFolderId: new Map(),
    rootFolderIds: [],
    plantRootId: null,
    allDescendantFolderIds: new Map(),
    allDescendantTagKeys: new Map(),
    parentMap: new Map(),
  };

  if (rows.length === 0) {
    return emptyIndex;
  }

  // -------------------------------------------------------------------------
  // Pass 1: Build foldersById, tagsByKey, tagsByName, tagKeysByFolderId
  // -------------------------------------------------------------------------
  const foldersById = new Map<number, IndexedFolder>();
  const tagsByKey = new Map<string, IndexedTag>();
  const tagsByName = new Map<string, IndexedTag[]>();
  const tagKeysByFolderId = new Map<number, string[]>();

  for (const row of rows) {
    const folderId = row.folder_id;

    // Create or reuse the IndexedFolder entry.
    // Folder metadata always comes from the FIRST row seen for this folder_id.
    if (!foldersById.has(folderId)) {
      foldersById.set(folderId, createIndexedFolder(row));
      tagKeysByFolderId.set(folderId, []);
    }

    // Process tag data if this is a tag row.
    const tagName = row.tag_name;
    if (tagName !== null && tagName !== undefined) {
      const compositeKey = `${folderId}::${tagName}`;

      if (tagsByKey.has(compositeKey)) {
        // Duplicate composite key — skip and warn.
        console.warn(
          `[FolderTreeIndex] Duplicate tag key "${compositeKey}" encountered. ` +
            'Skipping second occurrence.',
        );
      } else {
        // Level is not yet computed (Pass 2). Tags update their level in Pass 2.
        // We use 0 as a placeholder — it will be corrected when the folder's level is set.
        const indexedTag = createIndexedTag(row, tagName, 0);
        tagsByKey.set(compositeKey, indexedTag);

        // tagsByName: accumulate all tags sharing the same tag_name.
        const existingByName = tagsByName.get(tagName);
        if (existingByName !== undefined) {
          existingByName.push(indexedTag);
        } else {
          tagsByName.set(tagName, [indexedTag]);
        }

        // Append composite key to the folder's direct tag key list.
        const folder = foldersById.get(folderId);
        if (folder !== undefined) {
          folder.directTagKeys.push(compositeKey);
        }
        const keyList = tagKeysByFolderId.get(folderId);
        if (keyList !== undefined) {
          keyList.push(compositeKey);
        }
      }
    }
  }

  // -------------------------------------------------------------------------
  // Pass 2: BFS from plant root → level, ancestorIds, childFolderIds, rootFolderIds
  // -------------------------------------------------------------------------

  // Find the plant root (the folder whose parentId is null).
  let plantRootId: number | null = null;
  foldersById.forEach((folder, folderId) => {
    if (folder.parentId === null && plantRootId === null) {
      plantRootId = folderId;
    }
  });

  // Pre-build a parentId→childIds map for O(n) BFS (avoids O(n²) child scanning).
  const childIdsMap = new Map<number, number[]>();
  const parentMap = new Map<number, number>();

  foldersById.forEach((folder, folderId) => {
    if (folder.parentId !== null) {
      parentMap.set(folderId, folder.parentId);
      const existing = childIdsMap.get(folder.parentId);
      if (existing) {
        existing.push(folderId);
      } else {
        childIdsMap.set(folder.parentId, [folderId]);
      }
    }
  });

  const rootFolderIds: number[] = [];

  if (plantRootId !== null) {
    // BFS from plant root.
    type BFSEntry = { folderId: number; level: number; ancestorIds: number[] };
    const queue: BFSEntry[] = [{ folderId: plantRootId, level: 0, ancestorIds: [] }];
    const visited = new Set<number>();

    while (queue.length > 0) {
      const entry = queue.shift();
      if (entry === undefined) break;

      const { folderId, level, ancestorIds } = entry;
      if (visited.has(folderId)) continue;
      visited.add(folderId);

      const folder = foldersById.get(folderId);
      if (folder !== undefined) {
        folder.level = level;
        folder.ancestorIds = ancestorIds;
      }

      // Update level for all tags directly in this folder.
      const tagKeys = tagKeysByFolderId.get(folderId) ?? [];
      for (const tagKey of tagKeys) {
        const tag = tagsByKey.get(tagKey);
        if (tag !== undefined) {
          tag.level = level;
        }
      }

      const children = childIdsMap.get(folderId) ?? [];
      const childAncestors = [...ancestorIds, folderId];

      for (const childId of children) {
        if (level === 0) {
          rootFolderIds.push(childId);
        }
        queue.push({ folderId: childId, level: level + 1, ancestorIds: childAncestors });
        const childFolder = foldersById.get(childId);
        if (folder !== undefined && childFolder !== undefined) {
          folder.childFolderIds.push(childId);
        }
      }
    }

    // Handle orphan folders: folders whose parentId references a non-existent folder.
    foldersById.forEach((folder, folderId) => {
      if (!visited.has(folderId)) {
        const parentId = folder.parentId;
        if (parentId !== null && !foldersById.has(parentId)) {
          console.warn(
            `[FolderTreeIndex] Orphan folder id=${folderId} references non-existent ` +
              `parent_folder_id=${parentId}. Placing at root level as fallback.`,
          );
          folder.level = 1;
          folder.ancestorIds = plantRootId !== null ? [plantRootId] : [];
          rootFolderIds.push(folderId);

          // Update tags in orphan folder.
          const tagKeys = tagKeysByFolderId.get(folderId) ?? [];
          for (const tagKey of tagKeys) {
            const tag = tagsByKey.get(tagKey);
            if (tag !== undefined) {
              tag.level = 1;
            }
          }
        }
      }
    });
  }

  // -------------------------------------------------------------------------
  // Pass 3: Iterative post-order DFS → allDescendantFolderIds, allDescendantTagKeys
  // -------------------------------------------------------------------------
  const allDescendantFolderIds = new Map<number, number[]>();
  const allDescendantTagKeys = new Map<number, string[]>();

  if (plantRootId !== null) {
    // Iterative post-order traversal using an explicit stack.
    // We collect folders in post-order (children before parents) so that when
    // we process a folder its children's descendant sets are already computed.
    const postOrder: number[] = [];
    const stack: number[] = [plantRootId];

    while (stack.length > 0) {
      const folderId = stack.pop();
      if (folderId === undefined) break;
      postOrder.push(folderId);
      const folder = foldersById.get(folderId);
      if (folder !== undefined) {
        for (const childId of folder.childFolderIds) {
          stack.push(childId);
        }
      }
    }

    // Also include orphan folders that were placed at root level.
    for (const folderId of rootFolderIds) {
      const folder = foldersById.get(folderId);
      if (folder !== undefined && !postOrder.includes(folderId)) {
        postOrder.push(folderId);
      }
    }

    // Process in post-order (reverse of the stack-based pre-order above).
    for (let i = postOrder.length - 1; i >= 0; i--) {
      const folderId = postOrder[i];
      if (folderId === undefined) continue;

      const folder = foldersById.get(folderId);
      if (folder === undefined) continue;

      const descendantFolders: number[] = [];
      const descendantTagKeys: string[] = [...(tagKeysByFolderId.get(folderId) ?? [])];

      for (const childId of folder.childFolderIds) {
        descendantFolders.push(childId);

        const childDescFolders = allDescendantFolderIds.get(childId) ?? [];
        for (const id of childDescFolders) {
          descendantFolders.push(id);
        }

        const childDescTagKeys = allDescendantTagKeys.get(childId) ?? [];
        for (const key of childDescTagKeys) {
          descendantTagKeys.push(key);
        }
      }

      allDescendantFolderIds.set(folderId, descendantFolders);
      allDescendantTagKeys.set(folderId, descendantTagKeys);
    }
  }

  return {
    foldersById,
    tagsByKey,
    tagsByName,
    tagKeysByFolderId,
    rootFolderIds,
    plantRootId,
    allDescendantFolderIds,
    allDescendantTagKeys,
    parentMap,
  };
}

// ---------------------------------------------------------------------------
// Lookup utilities
// ---------------------------------------------------------------------------

/**
 * Return all IndexedTags whose tagName matches the given name.
 * If folderId is provided, narrows to only the tag in that specific folder.
 * Returns an empty array if no match is found.
 */
export function getTagByName(
  index: FolderTreeIndex,
  tagName: string,
  folderId?: number,
): IndexedTag[] {
  const matches = index.tagsByName.get(tagName) ?? [];
  if (folderId === undefined) {
    return matches;
  }
  return matches.filter((t) => t.folderId === folderId);
}

/**
 * Return the root-to-folder ordered chain of folder IDs for the given folder.
 * e.g. [0, 2000, 7] for folder 7 that is a child of 2000 which is a child of 0.
 * Returns an empty array if the folder is not in the index.
 */
export function getFolderPath(index: FolderTreeIndex, targetFolderId: number): number[] {
  const folder = index.foldersById.get(targetFolderId);
  if (folder === undefined) {
    return [];
  }
  return [...folder.ancestorIds, targetFolderId];
}

/**
 * Return the root-to-folder ordered chain of infra_display_name strings.
 * Parallel to getFolderPath — same length, same order.
 * Returns an empty array if the folder is not in the index.
 */
export function getReadableFolderPath(index: FolderTreeIndex, targetFolderId: number): string[] {
  const idPath = getFolderPath(index, targetFolderId);
  return idPath.reduce<string[]>((acc, id) => {
    const folder = index.foldersById.get(id);
    if (folder !== undefined) {
      acc.push(folder.infraDisplayName);
    }
    return acc;
  }, []);
}

/**
 * Return all IndexedFolders from root down to (and including) the given folder.
 * Returns an empty array if the folder is not in the index.
 */
export function getFolderHierarchy(index: FolderTreeIndex, folderId: number): IndexedFolder[] {
  const idPath = getFolderPath(index, folderId);
  return idPath.reduce<IndexedFolder[]>((acc, id) => {
    const folder = index.foldersById.get(id);
    if (folder !== undefined) {
      acc.push(folder);
    }
    return acc;
  }, []);
}

/**
 * Return all descendant folder IDs (recursive) for a given folder.
 * Returns an empty array if the folder is not in the index or has no descendants.
 */
export function getAllDescendantFolders(index: FolderTreeIndex, folderId: number): number[] {
  return index.allDescendantFolderIds.get(folderId) ?? [];
}

/**
 * Return all descendant tag composite keys (recursive) for a given folder.
 * Returns an empty array if the folder is not in the index or has no tags.
 */
export function getAllDescendantTagKeys(index: FolderTreeIndex, folderId: number): string[] {
  return index.allDescendantTagKeys.get(folderId) ?? [];
}

// ---------------------------------------------------------------------------
// Converters
// ---------------------------------------------------------------------------

/**
 * Convert an IndexedTag back to the legacy Tag interface.
 * Fields not available in the flat API (models, limit_ids, folderName) are
 * set to undefined to preserve backward compatibility.
 */
export function indexedTagToTag(indexed: IndexedTag): Tag {
  return {
    name: indexed.tagName,
    tag_display_name: indexed.tagDisplayName,
    description: indexed.tagDescription,
    uom: indexed.uom,
    is_numeric: indexed.isNumeric,
    is_continuous: indexed.isContinuous,
    is_sampled: indexed.isSampled,
    is_derived: indexed.isDerived,
    is_state: indexed.isState,
    tag_category_name: indexed.tagCategoryName,
    key: indexed.key,
    folder: indexed.folderId,
    models: undefined,
    limit_ids: undefined,
    folderName: undefined,
  };
}

/**
 * Convert an IndexedFolder to the legacy SubSystem interface.
 * Recursively converts child folders and direct tags.
 * Only use when backward compat with SubSystem-consuming code is required.
 */
export function indexedFolderToSubSystem(
  indexed: IndexedFolder,
  index: FolderTreeIndex,
): SubSystem {
  const tags: Tag[] = indexed.directTagKeys.reduce<Tag[]>((acc, key) => {
    const tag = index.tagsByKey.get(key);
    if (tag !== undefined) {
      acc.push(indexedTagToTag(tag));
    }
    return acc;
  }, []);

  const subfolders: SubSystem[] = indexed.childFolderIds.reduce<SubSystem[]>((acc, childId) => {
    const child = index.foldersById.get(childId);
    if (child !== undefined) {
      acc.push(indexedFolderToSubSystem(child, index));
    }
    return acc;
  }, []);

  return {
    folder: indexed.folderId,
    name: indexed.name,
    infra_display_name: indexed.infraDisplayName,
    description: indexed.description,
    has_model: indexed.hasModel,
    has_limits: indexed.hasLimits,
    subfolders,
    tags,
  };
}

/**
 * Convert an IndexedFolder to the legacy SubSystemParentRef interface.
 * Extends indexedFolderToSubSystem with infra_type_id, parentId, and parentName.
 */
export function indexedFolderToSubSystemParentRef(
  indexed: IndexedFolder,
  index: FolderTreeIndex,
): SubSystemParentRef {
  const base = indexedFolderToSubSystem(indexed, index);
  const parentId = index.parentMap.get(indexed.folderId);
  const parentFolder = parentId !== undefined ? index.foldersById.get(parentId) : undefined;

  return {
    ...base,
    infra_type_id: indexed.infraTypeId,
    parentId,
    parentName: parentFolder?.infraDisplayName,
  };
}
